import React, { useCallback, useState, useEffect } from 'react'
import Image from 'next/image'
import { IDiamond } from 'types/diamond.types'
import { Button } from '@components/ui'
import s from './TableListView.module.css'
import { diamondShapeImg } from '@utils/diamondShapes'
import {
  useDiamondData,
  useDiamondFilterAPI,
  useDiamondFilterState,
} from 'context/diamondFilterContext'
import {
  DiamondDataContextType,
  DiamondFilterAPIContextType,
  DiamondFilterContextType,
} from 'types/diamondFilter.types'
import { useShapeFilter } from 'context/shapeFilterContext'
import { formatUSDollar } from '@utils/format-price'
import { Pagination } from '@components/common'
import image from '../../icons/image.svg'
import video from '../../icons/video.svg'
import sortData from '../../ui/TableHeader/data.json'
import ArrowDownSVG from './ArrowDownSVG.svg'
import ArrowUpSVG from './ArrowUpSVG.svg'
import DiamondPreview from './DiamondPreview'
import { hasValidVideoUrlFormat } from '@components/yadav/ui/DiamondViewSlider/helper'
import cn from 'clsx'
import { getSeenDiamonds } from '@utils/seen-diamond'

const shapes = {
  RB: 'Round',
  PR: 'Princess',
  SQCUS: 'Square Cushion',
  CUS: 'Cushion',
  EC: 'Emerald',
  AS: 'Asscher',
  SQR: 'Square Radiant',
  OV: 'Oval',
  RAD: 'Radiant',
  MQ: 'Marquise',
  PS: 'Pear',
  HS: 'Heart',
  TR: 'Trillian',
}
const polsymm = {
  0: 'Poor',
  1: 'Fair',
  2: 'Good',
  3: 'Very Good',
  4: 'Excellent',
  5: 'Ideal',
}

let headers = [
  'View',
  'Compare',
  'Shape',
  'Price',
  'Weight',
  'Color',
  'Clarity',
  'Cut',
  'Report',
  ' ',
]

interface Props {
  data: IDiamond[]
  loading: boolean
  itemsToCompare: Partial<IDiamond>[]
  setItemsToCompare: (arg: Partial<IDiamond>[]) => void
  // sorted function, this work with data.json inside TableHeader folder
  onSort: (sort: string, direction: string, option: string) => void
}

const TableListView: React.FC<Props> = ({
  data,
  loading,
  itemsToCompare,
  setItemsToCompare,
  onSort,
}) => {
  const { diamondFilterState } =
    useDiamondFilterState() as DiamondFilterContextType
  const { onPageUpdate } = useDiamondFilterAPI() as DiamondFilterAPIContextType
  const { diamondMeta } = useDiamondData() as DiamondDataContextType
  const [loadingDiamond, setLoadingDiamond] = React.useState(false)
  const [openView, setOpenView] = useState<number | null>(null)
  const [seenDiamonds, setSeenDiamonds] = useState(getSeenDiamonds)
  const [previewedDiamonds, setPreviewedDiamonds] = useState<number[]>([])
  const [isMdScreenOrLarge, setIsMdScreenOrLarge] = useState<boolean>(false)
  const [diamondSelected, setDiamondSelected] = useState<number | null>(null)

  const { selectedCushionType } = useShapeFilter(); 

  useEffect(() => {
    setLoadingDiamond(false)
  }, [data])

  useEffect(() => {
    const handleResize = () => {
      const mediaQuery = window.matchMedia('(min-width: 768px)')
      headers = mediaQuery.matches
        ? [
            'View',
            'Compare',
            'Shape',
            'Price',
            'Weight',
            'Color',
            'Clarity',
            'Cut',
            'Report',
            ' ',
          ]
        : ['Shape', 'Cut', 'Weight', 'Price', 'Color', 'Clarity']

      setIsMdScreenOrLarge(mediaQuery.matches)
    }
    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  /* Sort management */
  const sortOptions = ['weight', 'price', 'color', 'cut', 'clarity']
  const [currentSort, setCurrentSort] = useState({
    sort: '',
    direction: '',
    option: '',
  })

  const handleOnSort = (header: string) => {
    const direction = diamondFilterState.direction === 'desc' ? 'asc' : 'desc'
    const matchingOption = sortData.options.find(
      (option) => option.sort === header && option.direction === direction
    )
    setCurrentSort(matchingOption || { sort: '', direction: '', option: '' })
    onSort(header, direction, matchingOption?.option || '')
  }
  /* End sort management */

  const onCompareClick = useCallback(
    (diamond: Partial<IDiamond>) => {
      if (!itemsToCompare.map((i) => i?.id).includes(diamond?.id)) {
        setItemsToCompare([...itemsToCompare, diamond])
      } else {
        setItemsToCompare(
          itemsToCompare.filter((i: Partial<IDiamond>) => i.id !== diamond.id)
        )
      }
    },
    [itemsToCompare, setItemsToCompare]
  )

  const onViewDiamond = (
    e:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    id: number
  ) => {
    e.preventDefault()
    if (openView === id) {
      setOpenView(null)
    } else {
      setOpenView(id)
    }
    if (!previewedDiamonds.includes(id)) {
      setPreviewedDiamonds([...previewedDiamonds, id])
    }
  }

  return (
    <>
      <table className={s.table}>
        <thead className={s.head}>
          <tr>
            {headers.map((header, index) => {
              return (
                <th
                  key={index}
                  colSpan={1}
                  className={cn(s.listViewHead, {
                    'cursor-pointer': sortOptions.includes(
                      header.toLowerCase()
                    ),
                  })}
                  onClick={
                    sortOptions.includes(header.toLowerCase())
                      ? () => handleOnSort(header.toLowerCase())
                      : undefined
                  }
                >
                  <div className="flex justify-center items-center gap-2">
                    <span>{header === 'Weight' ? 'Carat' : header}</span>
                    {sortOptions.includes(header.toLowerCase()) && (
                      <div onClick={() => handleOnSort(header.toLowerCase())}>
                        {currentSort.sort === header.toLowerCase() &&
                        currentSort.direction === 'asc' ? (
                          <div
                            className="cursor-pointer w-[12px] -ml-1 md:ml-0"
                            key={index}
                          >
                            <Image
                              src={ArrowUpSVG}
                              width={12}
                              height={8}
                              alt="arrow down"
                            />
                          </div>
                        ) : (
                          <div
                            className="cursor-pointer w-[10px] -ml-1 md:ml-0 md:w-[12px]"
                            key={index}
                          >
                            <Image
                              src={ArrowDownSVG}
                              width={12}
                              height={8}
                              alt="arrow down"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody className={s.listViewBody}>
          {data.map((diamond, i) => {
                const getFormattedShape = () => {
                  const formattedShape = shapes[diamond.shape as keyof typeof shapes];
                  if (formattedShape.includes('Cushion')) {
                    if (selectedCushionType === 'SQCUS') {
                      return 'Square Cushion';
                    } else if (selectedCushionType === 'ELCUS') {
                      return 'Elongated Cushion';
                    }
                  }
                  return formattedShape;
                };
            return (
              <React.Fragment key={diamond.id}>
                <tr
                  key={diamond.id}
                  className={cn(s.tr, {
                    [s.bgWhite]: i % 2 === 0,
                    [s.bgNormal]: i % 2 !== 0,
                    [s.seenDiamond]: seenDiamonds?.includes(diamond.id),
                    [s.selected]: diamond.id === diamondSelected,
                  })}
                  onClick={(e) => {
                    // Check if the clicked element is the comparison checkbox
                    const isCheckboxClicked =
                      (e.target as HTMLElement).tagName === 'INPUT' &&
                      (e.target as HTMLInputElement).type === 'checkbox'

                    if (diamond.id !== diamondSelected)
                      setDiamondSelected(diamond.id)
                    else setDiamondSelected(null)

                    if (!isCheckboxClicked) {
                      // If it's not the checkbox, execute onViewDiamond
                      onViewDiamond(e, diamond.id)
                    }
                  }}
                >
                  {/* View */}
                  <td className={s.cell}>
                    <div
                      className={cn(
                        'flex justify-center items-center scale-75 md:scale-100',
                        {
                          '-ml-[11px] md:-ml-[17px]':
                            (!diamond.has_image &&
                              diamond.videourl &&
                              !hasValidVideoUrlFormat(diamond.videourl)) ||
                            (!diamond.has_image && !diamond.videourl),
                        }
                      )}
                    >
                      <Image
                        src={
                          diamondShapeImg[
                            diamond.shape?.toUpperCase() as keyof typeof diamondShapeImg
                          ]
                        }
                        width={32}
                        height={32}
                        priority={true}
                        alt="diamond image"
                      />
                      {diamond.videourl &&
                      hasValidVideoUrlFormat(diamond.videourl) ? (
                        <Image
                          src={video}
                          width={17}
                          height={16}
                          priority={true}
                          className="relative"
                          alt="diamond video"
                        />
                      ) : (
                        diamond.has_image && (
                          <Image
                            src={image}
                            width={17}
                            height={16}
                            priority={true}
                            className="relative"
                            alt="diamond image"
                          />
                        )
                      )}
                    </div>
                  </td>
                  {/* Compare */}
                  <td className={!isMdScreenOrLarge ? 'hidden' : s.cell}>
                    <span className={s.tdComparison}>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="form-checkbox h-5 w-5 text-accent-9 focus:ring focus:ring-primary cursor-pointer"
                          checked={itemsToCompare
                            .map((i) => i?.id)
                            .includes(diamond.id)}
                          onChange={() => onCompareClick(diamond)}
                          aria-label="Compare"
                        />
                      </label>
                    </span>
                  </td>

                  {/* MOBILE CELLS*/}
                  {/* Cut */}
                  <td className={`${s.cell} md:hidden`}>{diamond.cut}</td>
                  {/* Weight */}
                  <td className={`${s.cell} md:hidden`}>{diamond.weight}</td>
                  {/* Price */}
                  <td className={`${s.cell} font-semibold md:hidden`}>
                    <span>
                      {formatUSDollar(Math.round(Number(diamond.price)))}
                    </span>
                  </td>
                  {/* Color */}
                  <td className={`${s.cell} font-semibold md:hidden`}>
                    {diamond.color}
                  </td>
                  {/* Clarity */}
                  <td className={`${s.cell} font-semibold md:hidden`}>
                    {diamond.clarity}
                  </td>

                  {/* DESKTOP CELLS*/}
                  {/* Shape */}
                  <td className={!isMdScreenOrLarge ? 'hidden' : s.cell}>
                    <span>{getFormattedShape()}</span>
                  </td>
                  {/* Price */}
                  <td className={!isMdScreenOrLarge ? 'hidden' : s.cell}>
                    <span>
                      {formatUSDollar(Math.round(Number(diamond.price)))}
                    </span>
                  </td>
                  {/* Weight */}
                  <td className={!isMdScreenOrLarge ? 'hidden' : s.cell}>
                    {diamond.weight}
                  </td>
                  {/* Color */}
                  <td className={!isMdScreenOrLarge ? 'hidden' : s.cell}>
                    {diamond.color}
                  </td>
                  {/* Clarity */}
                  <td className={!isMdScreenOrLarge ? 'hidden' : s.cell}>
                    {diamond.clarity}
                  </td>
                  {/* Cut */}
                  <td className={!isMdScreenOrLarge ? 'hidden' : s.cell}>
                    {diamond.cut}
                  </td>
                  {/* Lab */}
                  <td className={!isMdScreenOrLarge ? 'hidden' : s.cell}>
                    {diamond.lab}
                  </td>
                  {/* View Button */}
                  <td className={!isMdScreenOrLarge ? 'hidden' : s.cell}>
                    <Button
                      aria-label="View details"
                      className={s.btn}
                      variant="type1"
                      onClick={(e) => onViewDiamond(e, diamond.id)}
                    >
                      {openView === diamond.id ? 'Hide' : 'View'}
                    </Button>
                  </td>
                </tr>
                <tr
                  key={`${diamond.id}-preview`}
                  className={cn({ hidden: openView !== diamond.id })}
                >
                  <td colSpan={10}>
                    <DiamondPreview
                      diamond={diamond}
                      active={openView === diamond.id}
                      previewed={previewedDiamonds.includes(diamond.id)}
                    />
                  </td>
                </tr>
              </React.Fragment>
            )
          })}
        </tbody>
      </table>

      <div className="flex items-center	align-middle justify-end my-5">
        <div className="flex justify-end font-[inherit]">
          <Pagination
            top={700}
            setLoading={setLoadingDiamond}
            diamondFilterState={diamondFilterState}
            onPageUpdate={onPageUpdate}
            diamondMeta={diamondMeta.total_pages}
          />
        </div>
      </div>
    </>
  )
}

export default TableListView
